/**
 * Recruiter-facing timing labels for interview report Q&A cards (interview v1).
 *
 * time_spent = wall-clock (asked_at → answered_at).
 * Allotted time must match what the candidate UI enforces, driven by the same
 * env knobs as aptiview_interview (CRA apps use REACT_APP_* mirrors):
 *   - theoretical: REACT_APP_MAX_ANSWER_TIME_SECONDS (interview: NEXT_PUBLIC_MAX_ANSWER_TIME_SECONDS)
 *   - mcq:         REACT_APP_MCQ_TIMER_SECONDS (interview: NEXT_PUBLIC_MCQ_TIMER_SECONDS)
 *   - coding:      Question.time_limit minutes, else REACT_APP_CODING_TIME_SECONDS
 *
 * Never treat Question.time_limit=5 as the theoretical/MCQ allotment.
 * Both spent and given use: "6 sec", "1 min", "1 min 30 sec".
 */

/** Fallbacks only when env is unset — match aptiview_interview ChatPanel / MCQComponent / CodingAssignment. */
export const DEFAULT_THEORETICAL_ANSWER_SECONDS = 120;
export const DEFAULT_MCQ_TIMER_SECONDS = 90;
export const DEFAULT_CODING_TIME_SECONDS = 300;

function normalizeTimeLimitMinutes(timeLimit) {
  if (timeLimit == null || timeLimit === '') return null;
  const n = Number(timeLimit);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function normalizePositiveSeconds(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.floor(n);
}

function readEnvPositiveSeconds(keys) {
  if (typeof process === 'undefined' || !process.env) return null;
  for (const key of keys) {
    const parsed = normalizePositiveSeconds(process.env[key]);
    if (parsed != null) return parsed;
  }
  return null;
}

/** Theoretical / voice answer recording cap (seconds). */
export function getTheoreticalAnswerSecondsFromEnv() {
  return (
    readEnvPositiveSeconds([
      'REACT_APP_MAX_ANSWER_TIME_SECONDS',
      'NEXT_PUBLIC_MAX_ANSWER_TIME_SECONDS',
    ]) ?? DEFAULT_THEORETICAL_ANSWER_SECONDS
  );
}

/** MCQ countdown (seconds). */
export function getMcqTimerSecondsFromEnv() {
  return (
    readEnvPositiveSeconds([
      'REACT_APP_MCQ_TIMER_SECONDS',
      'NEXT_PUBLIC_MCQ_TIMER_SECONDS',
    ]) ?? DEFAULT_MCQ_TIMER_SECONDS
  );
}

/** Coding fallback when Question.time_limit is missing (seconds). */
export function getCodingTimeSecondsFromEnv() {
  return (
    readEnvPositiveSeconds([
      'REACT_APP_CODING_TIME_SECONDS',
      'NEXT_PUBLIC_CODING_TIME_SECONDS',
    ]) ?? DEFAULT_CODING_TIME_SECONDS
  );
}

/** Parse Django DurationField / clock strings (e.g. "0:10:13", "00:00:06") to seconds. */
export function parseTimeSpentToSeconds(timeSpent) {
  if (timeSpent == null || timeSpent === '') return null;
  if (typeof timeSpent === 'number' && Number.isFinite(timeSpent)) {
    return timeSpent < 0 ? null : Math.floor(timeSpent);
  }

  const raw = String(timeSpent).split('.')[0].trim();
  if (!raw) return null;

  if (raw.includes(':')) {
    const parts = raw.split(':').map((part) => Number(part));
    if (parts.length < 2 || parts.length > 3 || parts.some((n) => !Number.isFinite(n))) {
      return null;
    }
    if (parts.length === 3) {
      return Math.floor(parts[0] * 3600 + parts[1] * 60 + parts[2]);
    }
    return Math.floor(parts[0] * 60 + parts[1]);
  }

  const asNumber = Number(raw);
  if (!Number.isFinite(asNumber) || asNumber < 0) return null;
  return Math.floor(asNumber);
}

export function questionUsesAllottedTimeLimit(questionType) {
  return String(questionType || '').toLowerCase().trim() === 'coding';
}

function isTheoreticalQuestionType(questionType) {
  const type = String(questionType || '').toLowerCase().trim();
  return (
    type === 'concept' ||
    type === 'behavioral' ||
    type === 'technical' ||
    type === 'essay' ||
    type === 'short_answer' ||
    type === 'short answer' ||
    type === 'voice' ||
    type === 'general'
  );
}

/**
 * Shared human duration for spent and given values.
 * Examples: "0 sec", "6 sec", "1 min", "1 min 30 sec", "10 min 13 sec"
 */
export function formatDurationLabel(seconds) {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return null;
  const total = Math.floor(seconds);
  if (total === 0) return '0 sec';
  if (total % 60 === 0) {
    return `${total / 60} min`;
  }
  if (total > 60) {
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${mins} min ${secs} sec`;
  }
  return `${total} sec`;
}

/** @deprecated Prefer formatDurationLabel; kept for existing imports/tests. */
export function formatAllottedDuration(seconds) {
  const total = normalizePositiveSeconds(seconds);
  if (total == null) return null;
  return formatDurationLabel(total);
}

/**
 * Resolve seconds actually given to the candidate for this question type.
 * Prefers persisted timer fields, then env, then safe defaults.
 */
export function resolveAllottedSeconds(
  questionType,
  { timeLimit, timerSeconds, allottedSeconds } = {}
) {
  const explicit =
    normalizePositiveSeconds(allottedSeconds) ??
    normalizePositiveSeconds(timerSeconds);
  if (explicit != null) return explicit;

  const type = String(questionType || '').toLowerCase().trim();
  if (type === 'coding') {
    const mins = normalizeTimeLimitMinutes(timeLimit);
    if (mins != null) return mins * 60;
    return getCodingTimeSecondsFromEnv();
  }
  if (type === 'mcq') {
    return getMcqTimerSecondsFromEnv();
  }
  if (isTheoreticalQuestionType(type)) {
    return getTheoreticalAnswerSecondsFromEnv();
  }
  return null;
}

/**
 * @param {object} args
 * @param {string|null|undefined} args.questionType
 * @param {string|number|null|undefined} args.timeSpent
 * @param {string|number|null|undefined} args.timeLimit coding minutes
 * @param {string|number|null|undefined} [args.timerSeconds] MCQ override when stored
 * @param {string|number|null|undefined} [args.allottedSeconds] explicit override
 * @param {{ timeTaken?: string, timeLimit?: string, timeOnQuestion?: string, timeGiven?: string }} [args.labels]
 * @returns {string}
 */
export function formatQuestionTimeDisplay({
  questionType,
  timeSpent,
  timeLimit,
  timerSeconds,
  allottedSeconds,
  labels = {},
} = {}) {
  const spentSeconds = parseTimeSpentToSeconds(timeSpent);
  const spentLabel = formatDurationLabel(spentSeconds);
  const allotted = resolveAllottedSeconds(questionType, {
    timeLimit,
    timerSeconds,
    allottedSeconds,
  });
  const allottedLabel = formatDurationLabel(allotted);

  // Same labels for every question type so recruiters scan one pattern.
  const timeTakenLabel = labels.timeTaken || labels.timeOnQuestion || 'Time taken';
  const timeLimitLabel = labels.timeLimit || labels.timeGiven || 'Time limit';

  const parts = [];
  if (spentLabel) {
    parts.push(`${timeTakenLabel}: ${spentLabel}`);
  }
  if (allottedLabel) {
    parts.push(`${timeLimitLabel}: ${allottedLabel}`);
  }
  return parts.join(' · ');
}
